import { useState, useEffect } from "react";
import { Lock } from "lucide-react";
import axios from "axios";

// The password is NO LONGER stored here. Login is verified by the backend
// (POST /api/admin/login), which returns a random session token valid for 7 days.
// The token is stored in localStorage and attached to every API call by the
// axios interceptors in App.js.

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const AUTH_KEY = "wkbeast_auth_token";

// Used by pages that open backend URLs directly (PDF invoices, Excel backup) —
// those can't send headers, so they append ?admin_token= instead.
export const getAdminToken = () => localStorage.getItem(AUTH_KEY) || "";

export const useAuth = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Validate the saved session with the backend (a fixed marker string is no
    // longer enough — the server must recognize the token).
    const token = localStorage.getItem(AUTH_KEY);
    if (!token) {
      setIsLoading(false);
      return;
    }
    axios
      .get(`${API}/admin/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(() => setIsAuthenticated(true))
      .catch((err) => {
        // Only drop the session when the server explicitly rejects the token.
        // A network hiccup shouldn't log the admin out.
        if (err?.response?.status === 401) {
          localStorage.removeItem(AUTH_KEY);
        } else {
          setIsAuthenticated(true);
        }
      })
      .finally(() => setIsLoading(false));
  }, []);

  const login = async (password) => {
    try {
      const res = await axios.post(`${API}/admin/login`, { password });
      localStorage.setItem(AUTH_KEY, res.data.token);
      setIsAuthenticated(true);
      return true;
    } catch (err) {
      if (err?.response?.status === 429) return "locked";
      return false;
    }
  };

  const logout = () => {
    const token = localStorage.getItem(AUTH_KEY);
    if (token) {
      // Best-effort: revoke the session server-side too.
      axios
        .post(`${API}/admin/logout`, {}, { headers: { Authorization: `Bearer ${token}` } })
        .catch(() => {});
    }
    localStorage.removeItem(AUTH_KEY);
    setIsAuthenticated(false);
  };

  return { isAuthenticated, isLoading, login, logout };
};

export const LoginScreen = ({ onLogin }) => {
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [shake, setShake] = useState(false);
  const [busy, setBusy] = useState(false);
  const [showAdmin, setShowAdmin] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    const result = await onLogin(password);
    setBusy(false);
    if (result === true) {
      // Success - will redirect automatically
    } else {
      setError(
        result === "locked"
          ? "Too many wrong attempts. Wait 15 minutes and try again."
          : "Wrong password"
      );
      setShake(true);
      setTimeout(() => setShake(false), 500);
      setPassword("");
    }
  };

  const closeAdmin = () => {
    setShowAdmin(false);
    setPassword("");
    setError("");
  };

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center p-4">
      <div className={`w-full max-w-sm ${shake ? 'animate-shake' : ''}`}>
        <div className="text-center mb-8">
          <div className="w-20 h-20 rounded-2xl bg-[#00E054] flex items-center justify-center mx-auto mb-4 shadow-[0_0_40px_rgba(0,224,84,0.4)]">
            <span className="text-black font-bold text-3xl">W</span>
          </div>
          <h1 className="text-2xl font-bold text-white">WKBeast Farm</h1>
          <p className="text-gray-500 mt-2">Customer Portal</p>
        </div>

        {/* Customer Login - Primary/Prominent */}
        <a
          href="/portal"
          className="block w-full py-4 text-center bg-[#00E054] text-black font-bold rounded-xl hover:bg-[#00E054]/90 transition-all hover:shadow-[0_0_30px_rgba(0,224,84,0.5)] mb-3"
          data-testid="customer-portal-btn"
        >
          Customer Login
        </a>

        {/* New customer signup - straight to the signup form */}
        <a
          href="/portal?signup=1"
          className="block w-full py-4 text-center bg-transparent border-2 border-[#00E054]/60 text-[#00E054] font-bold rounded-xl hover:bg-[#00E054]/10 hover:border-[#00E054] transition-all mb-4"
          data-testid="customer-signup-btn"
        >
          Create Account
        </a>

        <p className="text-center text-gray-500 text-sm mb-6">
          View your machines, payments & earnings
        </p>

      </div>

      {/* Discreet admin entry — small link pinned to the bottom corner */}
      <button
        onClick={() => setShowAdmin(true)}
        className="fixed bottom-4 right-5 flex items-center gap-1.5 text-gray-700 text-xs hover:text-gray-400 transition-colors"
        data-testid="admin-access-link"
      >
        <Lock size={12} />
        Admin
      </button>

      {/* Admin login popup */}
      {showAdmin && (
        <div
          className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 z-50"
          onClick={closeAdmin}
        >
          <div
            className={`w-full max-w-xs bg-[#0F0F0F] border border-[#27272A] rounded-2xl p-6 ${shake ? 'animate-shake' : ''}`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-2 mb-4">
              <Lock className="text-gray-500" size={16} />
              <h2 className="text-white text-sm font-bold">Admin Access</h2>
            </div>
            <form onSubmit={handleSubmit} className="space-y-3">
              <input
                type="password"
                autoFocus
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setError("");
                }}
                placeholder="Admin password"
                className={`w-full px-4 py-3 bg-[#050505] border ${
                  error ? 'border-red-500' : 'border-[#27272A]'
                } rounded-lg text-white text-sm tracking-[0.3em] placeholder:tracking-normal placeholder:text-xs focus:outline-none focus:border-[#00E054]/40 transition-colors`}
                data-testid="password-input"
              />

              {error && (
                <p className="text-red-500 text-xs text-center">{error}</p>
              )}

              <button
                type="submit"
                disabled={busy}
                className="w-full py-2.5 bg-[#00E054] text-black text-sm font-bold rounded-lg hover:bg-[#00E054]/90 transition-colors disabled:opacity-50"
                data-testid="login-btn"
              >
                {busy ? "Checking..." : "Unlock Admin"}
              </button>
              <button
                type="button"
                onClick={closeAdmin}
                className="w-full py-2 text-gray-500 text-xs hover:text-white transition-colors"
                data-testid="admin-cancel-btn"
              >
                Cancel
              </button>
            </form>
          </div>
        </div>
      )}

      <style>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          20%, 60% { transform: translateX(-10px); }
          40%, 80% { transform: translateX(10px); }
        }
        .animate-shake {
          animation: shake 0.5s ease-in-out;
        }
      `}</style>
    </div>
  );
};
